import React, { useState, useMemo } from "react";
import DashboardLayout from "../UI/Layout/DashboardLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt, faPlus, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const gatewayOptions = [
    { name: "UPI",      subtitle: "Direct Bank Transfer" },
    { name: "Razorpay", subtitle: "UPI & Indian Cards" },
    { name: "PayU",     subtitle: "Indian Payment Gateway" },
    { name: "Zaakpay",  subtitle: "Secure Payments" },
];

const emptyForm = { gateway: "", configuration: "" };

const STATUSES       = ["Success", "Pending", "Failed", "Refunded"];
const PAYMENT_METHODS = ["UPI", "Razorpay", "PayU", "Zaakpay"];

const emptyFilters = {
    txnId: "", orderId: "", customerName: "", whatsappNumber: "",
    paymentMethod: "", dateFrom: "", dateTo: "", status: "",
};

const sampleTransactions = [
    { txnId: "TXN20240601001", orderId: "ORD1001", customerName: "Amit Sharma",    whatsappNumber: "9876543210", paymentMethod: "UPI",      dateTime: "2024-06-01 10:30", status: "Success",  amount: 1500 },
    { txnId: "TXN20240601002", orderId: "ORD1002", customerName: "Priya Singh",    whatsappNumber: "9123456789", paymentMethod: "Razorpay", dateTime: "2024-06-01 11:15", status: "Pending",  amount: 2500 },
    { txnId: "TXN20240601003", orderId: "ORD1003", customerName: "Rahul Verma",    whatsappNumber: "9988776655", paymentMethod: "PayU",     dateTime: "2024-06-01 12:00", status: "Failed",   amount: 800  },
    { txnId: "TXN20240601004", orderId: "ORD1004", customerName: "Sneha Patel",    whatsappNumber: "8877665544", paymentMethod: "Zaakpay",  dateTime: "2024-06-01 13:30", status: "Success",  amount: 3200 },
    { txnId: "TXN20240601005", orderId: "ORD1005", customerName: "Karan Mehta",    whatsappNumber: "7766554433", paymentMethod: "UPI",      dateTime: "2024-06-01 14:00", status: "Refunded", amount: 1200 },
    { txnId: "TXN20240601006", orderId: "ORD1006", customerName: "Neha Gupta",     whatsappNumber: "9654321098", paymentMethod: "Razorpay", dateTime: "2024-06-02 09:00", status: "Success",  amount: 4500 },
    { txnId: "TXN20240601007", orderId: "ORD1007", customerName: "Vikram Joshi",   whatsappNumber: "9543210987", paymentMethod: "UPI",      dateTime: "2024-06-02 10:30", status: "Pending",  amount: 900  },
    { txnId: "TXN20240601008", orderId: "ORD1008", customerName: "Anita Desai",    whatsappNumber: "9432109876", paymentMethod: "PayU",     dateTime: "2024-06-02 11:00", status: "Failed",   amount: 1800 },
    { txnId: "TXN20240601009", orderId: "ORD1009", customerName: "Suresh Kumar",   whatsappNumber: "9321098765", paymentMethod: "Zaakpay",  dateTime: "2024-06-02 12:30", status: "Success",  amount: 6000 },
    { txnId: "TXN20240601010", orderId: "ORD1010", customerName: "Pooja Agarwal",  whatsappNumber: "9210987654", paymentMethod: "Razorpay", dateTime: "2024-06-02 14:00", status: "Refunded", amount: 2100 },
    { txnId: "TXN20240601011", orderId: "ORD1011", customerName: "Deepak Nair",    whatsappNumber: "8109876543", paymentMethod: "UPI",      dateTime: "2024-06-03 09:30", status: "Success",  amount: 3500 },
    { txnId: "TXN20240601012", orderId: "ORD1012", customerName: "Meera Krishnan", whatsappNumber: "8098765432", paymentMethod: "PayU",     dateTime: "2024-06-03 10:00", status: "Pending",  amount: 750  },
];

const STATUS_STYLE = {
    Success:  { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
    Pending:  { color: "#92400e", bg: "#fffbeb", border: "#fde68a" },
    Failed:   { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
    Refunded: { color: "#5b21b6", bg: "#f5f3ff", border: "#ddd6fe" },
};

const ANALYTICS_CONFIG = [
    { key: "Total",    label: "Total",    icon: "≡" },
    { key: "Success",  label: "Success",  icon: "✓" },
    { key: "Pending",  label: "Pending",  icon: "⏳" },
    { key: "Failed",   label: "Failed",   icon: "✕" },
    { key: "Refunded", label: "Refunded", icon: "↩" },
];

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLE[status] || { color: "#374151", bg: "#f3f4f6", border: "#e5e7eb" };
    return (
        <span style={{
            display: "inline-block",
            background: s.bg,
            color: s.color,
            border: `1px solid ${s.border}`,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 4,
            padding: "2px 9px",
            letterSpacing: "0.02em",
        }}>
            {status}
        </span>
    );
};

const inputStyle = {
    fontSize: 13,
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "6px 10px",
    width: "100%",
    outline: "none",
    background: "#fff",
    color: "#111827",
};

const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    display: "block",
    marginBottom: 5,
};

const PaymentGatewaySetup = () => {
    const [list, setList]               = useState([]);
    const [modal, setModal]             = useState(false);
    const [form, setForm]               = useState(emptyForm);
    const [editIndex, setEditIndex]     = useState(null);
    const [error, setError]             = useState("");
    const [filters, setFilters]         = useState(emptyFilters);
    const [appliedFilters, setApplied]  = useState(emptyFilters);

    const openAdd   = () => { setForm(emptyForm); setEditIndex(null); setError(""); setModal(true); };
    const openEdit  = (i) => { setForm({ ...list[i] }); setEditIndex(i); setError(""); setModal(true); };
    const closeModal = () => { setModal(false); setForm(emptyForm); setEditIndex(null); setError(""); };

    const handleSave = () => {
        if (!form.gateway)              return setError("Please select a payment provider.");
        if (!form.configuration.trim()) return setError("Please enter payment configuration.");
        if (editIndex !== null) {
            const updated = [...list];
            updated[editIndex] = { ...form };
            setList(updated);
        } else {
            setList(prev => [...prev, { ...form }]);
        }
        closeModal();
    };

    const handleDelete = (i) => setList(list.filter((_, idx) => idx !== i));

    const handleFilterChange = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
    const applyFilters = () => setApplied({ ...filters });
    const clearFilters = () => { setFilters(emptyFilters); setApplied(emptyFilters); };

    const filteredTxns = useMemo(() => sampleTransactions.filter(t => {
        const f = appliedFilters;
        if (f.txnId         && !t.txnId.toLowerCase().includes(f.txnId.toLowerCase()))               return false;
        if (f.orderId        && !t.orderId.toLowerCase().includes(f.orderId.toLowerCase()))           return false;
        if (f.customerName   && !t.customerName.toLowerCase().includes(f.customerName.toLowerCase())) return false;
        if (f.whatsappNumber && !t.whatsappNumber.includes(f.whatsappNumber))                         return false;
        if (f.paymentMethod  && t.paymentMethod !== f.paymentMethod)                                  return false;
        if (f.status         && t.status !== f.status)                                                return false;
        if (f.dateFrom       && t.dateTime < f.dateFrom)                                              return false;
        if (f.dateTo         && t.dateTime > f.dateTo + " 23:59")                                     return false;
        return true;
    }), [appliedFilters]);

    const analytics = useMemo(() => {
        const r = { Total: { count: 0, amount: 0 } };
        STATUSES.forEach(s => { r[s] = { count: 0, amount: 0 }; });
        filteredTxns.forEach(t => {
            r.Total.count++;
            r.Total.amount += t.amount;
            if (r[t.status]) { r[t.status].count++; r[t.status].amount += t.amount; }
        });
        return r;
    }, [filteredTxns]);

    return (
        <DashboardLayout>
            <main className="content">
                <div className="container-fluid p-0">

                    {/* ── Page Header ── */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 700, color: "#111827", fontSize: 17 }}>Payment Gateway Setup</h5>
                            <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Manage payment gateways and view transaction reports</p>
                        </div>
                        <button
                            onClick={openAdd}
                            style={{ display: "flex", alignItems: "center", gap: 6, background: "#111827", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} />
                            Add Gateway
                        </button>
                    </div>

                    {/* ── Analytics Cards ── */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
                        {ANALYTICS_CONFIG.map(cfg => (
                            <div key={cfg.key} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 18px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        {cfg.label}
                                    </span>
                                    <span style={{ fontSize: 14, color: "#d1d5db" }}>{cfg.icon}</span>
                                </div>
                                <div style={{ fontSize: 26, fontWeight: 800, color: "#111827", lineHeight: 1, marginBottom: 4 }}>
                                    {analytics[cfg.key]?.count ?? 0}
                                </div>
                                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
                                    ₹{(analytics[cfg.key]?.amount ?? 0).toLocaleString("en-IN")}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Filters ── */}
                    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 18, padding: "16px 18px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 12 }}>Filters</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
                            {[
                                { key: "txnId",         label: "Transaction ID",   type: "text"  },
                                { key: "orderId",        label: "Order ID",         type: "text"  },
                                { key: "customerName",   label: "Customer Name",    type: "text"  },
                                { key: "whatsappNumber", label: "WhatsApp Number",  type: "text"  },
                                { key: "dateFrom",       label: "Date From",        type: "date"  },
                                { key: "dateTo",         label: "Date To",          type: "date"  },
                            ].map(f => (
                                <div key={f.key}>
                                    <label style={labelStyle}>{f.label}</label>
                                    <input
                                        type={f.type}
                                        style={inputStyle}
                                        placeholder={f.type === "text" ? "Search..." : ""}
                                        value={filters[f.key]}
                                        onChange={e => handleFilterChange(f.key, e.target.value)}
                                    />
                                </div>
                            ))}
                            <div>
                                <label style={labelStyle}>Payment Method</label>
                                <select style={inputStyle} value={filters.paymentMethod} onChange={e => handleFilterChange("paymentMethod", e.target.value)}>
                                    <option value="">All</option>
                                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Status</label>
                                <select style={inputStyle} value={filters.status} onChange={e => handleFilterChange("status", e.target.value)}>
                                    <option value="">All</option>
                                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                            <button onClick={clearFilters} style={{ background: "#fff", border: "1px solid #d1d5db", color: "#374151", borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                                <FontAwesomeIcon icon={faTimes} style={{ marginRight: 5 }} /> Clear
                            </button>
                            <button onClick={applyFilters} style={{ background: "#111827", border: "none", color: "#fff", borderRadius: 6, padding: "6px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                <FontAwesomeIcon icon={faSearch} style={{ marginRight: 5 }} /> Apply Filter
                            </button>
                        </div>
                    </div>

                    {/* ── Transaction Table ── */}
                    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #f3f4f6" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Transaction Reports</span>
                            <span style={{ fontSize: 12, color: "#9ca3af", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 20, padding: "2px 10px" }}>
                                {filteredTxns.length} record{filteredTxns.length !== 1 ? "s" : ""}
                            </span>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                <thead style={{ background: "#f9fafb" }}>
                                    <tr>
                                        {["#", "Transaction ID", "Order ID", "Customer Name", "WhatsApp Number", "Payment Method", "Date & Time", "Status", "Amount (₹)"].map((h, i) => (
                                            <th key={h} style={{ padding: "10px 14px", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap", textAlign: i === 8 ? "right" : "left" }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTxns.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} style={{ padding: 32, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No transactions found.</td>
                                        </tr>
                                    ) : filteredTxns.map((t, i) => (
                                        <tr key={t.txnId} style={{ borderBottom: "1px solid #f3f4f6", background: "#fff" }}>
                                            <td style={{ padding: "10px 14px", color: "#9ca3af" }}>{i + 1}</td>
                                            <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12, color: "#374151", fontWeight: 600 }}>{t.txnId}</td>
                                            <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12, color: "#374151" }}>{t.orderId}</td>
                                            <td style={{ padding: "10px 14px", color: "#111827", fontWeight: 500 }}>{t.customerName}</td>
                                            <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12, color: "#374151" }}>{t.whatsappNumber}</td>
                                            <td style={{ padding: "10px 14px" }}>
                                                <span style={{ background: "#f3f4f6", color: "#374151", fontSize: 11, fontWeight: 600, borderRadius: 4, padding: "2px 8px", border: "1px solid #e5e7eb" }}>
                                                    {t.paymentMethod}
                                                </span>
                                            </td>
                                            <td style={{ padding: "10px 14px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>{t.dateTime}</td>
                                            <td style={{ padding: "10px 14px" }}><StatusBadge status={t.status} /></td>
                                            <td style={{ padding: "10px 14px", fontWeight: 700, textAlign: "right", color: "#111827" }}>
                                                {t.amount.toLocaleString("en-IN")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {filteredTxns.length > 0 && (
                                    <tfoot>
                                        <tr style={{ background: "#f9fafb", borderTop: "2px solid #e5e7eb" }}>
                                            <td colSpan={8} style={{ padding: "10px 14px", fontWeight: 600, textAlign: "right", fontSize: 12, color: "#374151" }}>Total Amount</td>
                                            <td style={{ padding: "10px 14px", fontWeight: 800, textAlign: "right", fontSize: 14, color: "#111827" }}>
                                                ₹{filteredTxns.reduce((s, t) => s + t.amount, 0).toLocaleString("en-IN")}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div>

                {/* ── Add / Edit Modal ── */}
                {modal && (
                    <div
                        style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.4)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
                        onClick={closeModal}
                    >
                        <div
                            style={{ background: "#fff", borderRadius: 12, padding: "28px", width: 540, boxShadow: "0 20px 48px rgba(0,0,0,0.14)" }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #e5e7eb" }}>
                                <div>
                                    <h6 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#111827" }}>
                                        {editIndex !== null ? "Edit Payment Gateway" : "Add Payment Gateway"}
                                    </h6>
                                    <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Configure your merchant gateway credentials</p>
                                </div>
                                <button onClick={closeModal} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af", lineHeight: 1, padding: "0 2px" }}>
                                    &times;
                                </button>
                            </div>

                            {error && (
                                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: 12, borderRadius: 6, padding: "8px 12px", marginBottom: 16 }}>
                                    {error}
                                </div>
                            )}

                            {/* Provider Selection */}
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>Payment Provider</label>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                                    {gatewayOptions.map(opt => (
                                        <div
                                            key={opt.name}
                                            onClick={() => setForm({ ...form, gateway: opt.name })}
                                            style={{
                                                border: form.gateway === opt.name ? "1.5px solid #111827" : "1px solid #e5e7eb",
                                                background: form.gateway === opt.name ? "#f9fafb" : "#fff",
                                                borderRadius: 8, padding: "10px 10px", cursor: "pointer",
                                            }}
                                        >
                                            <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", marginBottom: 2 }}>{opt.name}</div>
                                            <div style={{ fontSize: 10, color: "#9ca3af", lineHeight: 1.3 }}>{opt.subtitle}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Configuration Input */}
                            <div style={{ marginBottom: 24 }}>
                                <label style={labelStyle}>
                                    Payment Configuration <span style={{ color: "#b91c1c" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    style={{ ...inputStyle, padding: "8px 12px" }}
                                    placeholder="Enter payment configuration"
                                    value={form.configuration}
                                    onChange={e => setForm({ ...form, configuration: e.target.value })}
                                />
                            </div>

                            {/* Modal Footer */}
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
                                <button onClick={closeModal} style={{ background: "#fff", border: "1px solid #d1d5db", color: "#374151", borderRadius: 7, padding: "7px 18px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                                    Cancel
                                </button>
                                <button onClick={handleSave} style={{ background: "#111827", border: "none", color: "#fff", borderRadius: 7, padding: "7px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                    {editIndex !== null ? "Update" : "Save Configuration"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </DashboardLayout>
    );
};

export default PaymentGatewaySetup;
